import { useUser } from '@descope/vue-sdk';
import * as Sentry from '@sentry/vue';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import apiClient from '@/services/api_client';
import { handleMicrosoftConnection } from '@/services/auth_microsoft';
import { useConfigCatStore } from '@/stores/configCat';

export const useUserStore = defineStore('user', () => {
    const configCatStore = useConfigCatStore();
    const { user: descopeUser } = useUser();
    const tenants = ref<any[]>([]);
    const name = ref<string>('');
    const displayName = ref<string>('');
    const firstName = ref<string>('');
    const lastName = ref<string>('');
    const email = ref<string>('');
    const phoneNumber = ref<string>('');
    const companyName = ref<string>('');
    const userId = ref<string>('');
    const attachedFilesName = ref<string>('');

    function splitFullName(fullName: string) {
        const trimmed = fullName.trim();
        if (!trimmed) {
            return { first: '', last: '' };
        }

        const parts = trimmed.split(/\s+/);
        return {
            first: parts[0] ?? '',
            last: parts.slice(1).join(' ')
        };
    }

    function setDisplayName(value: string) {
        displayName.value = value.trim();
    }

    watch(descopeUser, async (newValue, oldValue) => {
        if (newValue && newValue !== oldValue) {
            tenants.value = newValue.userTenants || [];
            name.value = newValue.name || '';
            const splitName = splitFullName(newValue.name || '');
            firstName.value = newValue.givenName || splitName.first || '';
            lastName.value = newValue.familyName || splitName.last || '';
            displayName.value = firstName.value || splitName.first || '';
            email.value = newValue.email || '';
            phoneNumber.value = newValue.phone || '';
            userId.value = newValue.userId || '';
            configCatStore.fetchConfigCatFlags(newValue);
            Sentry.setUser({
                id: userId.value,
                email: email.value,
                username: name.value
            });
            attachedFilesName.value =
                'attached_files_' + userId.value.toLowerCase();
        }
    });

    async function fetchTeamsUser() {
        const response = await apiClient.get('/teams/me');
        const result = await response.json();

        name.value = result.name;
        email.value = result.email;
        tenants.value = result.userTenants;
        userId.value = result.userId;
        configCatStore.fetchConfigCatFlags(result);
        attachedFilesName.value =
            'attached_files_' + userId.value.toLowerCase();
    }

    async function fetchMicrosoftUser(
        microsoft_app_id: string,
        azure_tenant_id: string,
        microsoftRedirectHash: string,
        tenant_name: string
    ) {
        try {
            const account = await handleMicrosoftConnection(
                microsoft_app_id,
                azure_tenant_id,
                '',
                microsoftRedirectHash
            );

            if (!account) {
                console.log(
                    'No account returned from Microsoft authentication'
                );
                return undefined;
            }

            name.value = account?.name || '';
            const splitName = splitFullName(account?.name || '');
            firstName.value = splitName.first;
            lastName.value = splitName.last;
            displayName.value = splitName.first;
            email.value = account?.username.toLowerCase() || '';
            phoneNumber.value = '';
            companyName.value = '';
            userId.value = account?.localAccountId || '';

            tenants.value = [
                {
                    tenantId: azure_tenant_id,
                    tenantName: tenant_name
                }
            ];

            configCatStore.fetchConfigCatFlags({
                userId: userId.value,
                email: email.value
            });
            attachedFilesName.value =
                'attached_files_' + userId.value.toLowerCase();
            return account;
        } catch (error) {
            console.error('Error fetching Microsoft user:', error);
            return undefined;
        }
    }

    return {
        userId,
        tenants,
        name,
        displayName,
        firstName,
        lastName,
        email,
        phoneNumber,
        companyName,
        attachedFilesName,
        setDisplayName,
        fetchTeamsUser,
        fetchMicrosoftUser
    };
});
